/**
 * Shared, pure level validation used OUTSIDE the open editor (design §18/§19): the roster
 * scan in DesignWorkspace re-checks every level when the 3Cs change or Content refs move,
 * so tightening the feel contract or renaming Content surfaces the blast radius in the nav
 * without opening each level. Same registry + schema + validate path the editor uses.
 */
import { registerBuiltinKinds, getKind, type ThreeCsProfile } from '@volley/crucible-levels';

let kindsReady = false;
function ensureKinds(): void {
  if (kindsReady) return;
  try {
    registerBuiltinKinds();
  } catch {
    // already registered in this module instance
  }
  kindsReady = true;
}

export interface LevelScan {
  /** Schema-error or validator-error count under the given 3Cs. */
  errors: number;
  /** How many refs point at Content items not in the palette (dangling). */
  orphanRefs: number;
}

/** Validate one level's already-parsed data. Never throws (unknown kind → zeroes). */
export function scanLevelData(
  kind: string,
  data: unknown,
  threeCs: ThreeCsProfile,
  palette?: ReadonlySet<string>,
  /** 0-based progression position — lets pace-ramped kinds validate at the level's real
   *  effective speed instead of the base. Omit for levels with no known order. */
  levelIndex?: number,
  /** The level's routing tags — lets kinds flag intent mismatches (boss level w/o encounter). */
  tags?: readonly string[],
): LevelScan {
  ensureKinds();
  try {
    const k = getKind(kind);
    const parsed = k.schema.safeParse(data);
    if (!parsed.success) return { errors: parsed.error.issues.length, orphanRefs: 0 };
    const findings = k.validate(parsed.data, {
      threeCs: k.consumesThreeCs ? threeCs : undefined,
      palette,
      levelIndex,
      tags,
    });
    return {
      errors: findings.filter((f) => f.severity === 'error').length,
      orphanRefs: findings.filter((f) => f.code === 'orphan-ref').length,
    };
  } catch {
    return { errors: 0, orphanRefs: 0 };
  }
}
